/**
 * Camera asset hotspots API — Administration → Camera management only.
 * Base: `GET/POST/PUT/DELETE /api/cameras/:camera_id/hotspots`
 * Media: `{NODE_API}/media/hotspots/...`
 */

import { ApiRequestError } from "./authDevApiClient.js";
import { getNodeApiBaseUrl } from "./nodeApiBaseUrl.ts";
import type {
  CameraAssetHotspot,
  CameraAssetHotspotInput,
  CameraAssetHotspotShowType,
} from "./cameraHotspots.ts";
import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";

export type CameraHotspotListResponse = {
  camera_id: string;
  count: number;
  hotspots: CameraAssetHotspot[];
};

export type CreateCameraHotspotJsonBody = {
  asset_name: string;
  show_type: "website" | "youtube" | "camera" | "text" | "api_url";
  yaw_deg: number;
  pitch_deg: number;
  pin_direction_deg?: number;
  content_url: string;
};

export type CameraHotspotMetadataBody = {
  asset_name: string;
  show_type: "image" | "pdf";
  yaw_deg: number;
  pitch_deg: number;
  pin_direction_deg?: number;
};

function buildUrl(path: string): string {
  const base = getNodeApiBaseUrl();
  let p = path.startsWith("/") ? path : `/${path}`;
  if (/\/api$/i.test(base) && /^\/api\//i.test(p)) {
    p = p.slice(4);
    if (!p.startsWith("/")) p = `/${p}`;
  }
  return `${base}${p}`;
}

function hotspotsPath(cameraId: string, hotspotId?: string): string {
  const cam = encodeURIComponent(String(cameraId || "").trim());
  const base = `/api/cameras/${cam}/hotspots`;
  return hotspotId
    ? `${base}/${encodeURIComponent(String(hotspotId).trim())}`
    : base;
}

function parseError(data: unknown, status: number): string {
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const nested =
      o.error && typeof o.error === "object" && !Array.isArray(o.error)
        ? (o.error as Record<string, unknown>)
        : null;
    return (
      (typeof o.message === "string" && o.message) ||
      (nested && typeof nested.message === "string" && nested.message) ||
      (typeof o.error === "string" && o.error) ||
      `Request failed (${status})`
    );
  }
  return `Request failed (${status})`;
}

function throwIfEnvelopeError(data: unknown, status: number): void {
  if (!data || typeof data !== "object") return;
  const o = data as Record<string, unknown>;
  if (o.success === false) {
    throw new ApiRequestError(parseError(data, status), "REQUEST_FAILED", status);
  }
}

async function parseJsonResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function authHeaders(json = false): Record<string, string> {
  const token = tokenManager.getToken();
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
}

async function apiFetch(
  path: string,
  init: RequestInit & { jsonBody?: unknown } = {},
): Promise<unknown> {
  const { jsonBody, headers: extraHeaders, ...rest } = init;
  const headers: Record<string, string> = {
    ...authHeaders(jsonBody !== undefined),
    ...(extraHeaders as Record<string, string> | undefined),
  };

  const res = await fetch(buildUrl(path), {
    ...rest,
    headers,
    body:
      jsonBody !== undefined
        ? JSON.stringify(jsonBody)
        : (rest.body as BodyInit | undefined),
  });

  const data = await parseJsonResponse(res);
  if (!res.ok) {
    throw new ApiRequestError(parseError(data, res.status), "REQUEST_FAILED", res.status);
  }
  throwIfEnvelopeError(data, res.status);
  return data;
}

/** Prepend API host for `media/hotspots/...` paths from the backend. */
export function resolveCameraHotspotMediaUrl(
  url: string | undefined | null,
): string {
  if (url == null || url === "") return "";
  const s = String(url).trim();
  if (!s) return "";
  if (/^(https?:|blob:|data:)/i.test(s)) return s;
  const base = getNodeApiBaseUrl();
  const path = s.replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

function unwrapHotspotEntity(body: unknown): unknown {
  if (body == null) return null;
  if (Array.isArray(body)) return body[0] ?? null;
  const o = body as Record<string, unknown>;
  if (o.data && typeof o.data === "object" && !Array.isArray(o.data)) {
    return o.data;
  }
  if (o.hotspot && typeof o.hotspot === "object") return o.hotspot;
  return body;
}

function unwrapHotspotList(body: unknown): CameraAssetHotspot[] {
  if (Array.isArray(body)) {
    return body
      .map(mapCameraHotspotFromApi)
      .filter((h): h is CameraAssetHotspot => h != null);
  }
  const o = body as Record<string, unknown> | null;
  const list = o?.hotspots ?? o?.data;
  if (!Array.isArray(list)) return [];
  return list
    .map(mapCameraHotspotFromApi)
    .filter((h): h is CameraAssetHotspot => h != null);
}

export function mapCameraHotspotFromApi(raw: unknown): CameraAssetHotspot | null {
  if (!raw || typeof raw !== "object") return null;
  const h = raw as Record<string, unknown>;
  const id = String(h.id ?? "").trim();
  const camera_id = String(h.camera_id ?? h.cameraId ?? "").trim();
  if (!id || !camera_id) return null;

  const yaw = Number(h.yaw_deg);
  const pitch = Number(h.pitch_deg);
  if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return null;

  const showType = String(h.show_type ?? "image").trim() as CameraAssetHotspotShowType;

  return {
    id,
    camera_id,
    yaw_deg: yaw,
    pitch_deg: pitch,
    pin_direction_deg: Number(h.pin_direction_deg) || 0,
    asset_name: String(h.asset_name ?? "").trim(),
    show_type:
      showType === "image" ||
      showType === "pdf" ||
      showType === "website" ||
      showType === "youtube" ||
      showType === "camera" ||
      showType === "text" ||
      showType === "api_url"
        ? showType
        : "image",
    content_url: String(h.content_url ?? "").trim(),
    image_url:
      h.image_url != null && String(h.image_url).trim()
        ? String(h.image_url).trim()
        : undefined,
  };
}

/** GET /api/cameras/:camera_id/hotspots */
export async function fetchCameraHotspots(
  cameraId: string,
): Promise<CameraAssetHotspot[]> {
  const cid = String(cameraId || "").trim();
  if (!cid) return [];
  const data = await apiFetch(hotspotsPath(cid), { method: "GET" });
  return unwrapHotspotList(data);
}

/** GET /api/cameras/:camera_id/hotspots/:hotspot_id */
export async function fetchCameraHotspot(
  cameraId: string,
  hotspotId: string,
): Promise<CameraAssetHotspot> {
  const data = await apiFetch(hotspotsPath(cameraId, hotspotId), { method: "GET" });
  const mapped = mapCameraHotspotFromApi(unwrapHotspotEntity(data));
  if (!mapped) {
    throw new Error("Hotspot not found in response.");
  }
  return mapped;
}

/** POST /api/cameras/:camera_id/hotspots */
export async function createCameraHotspot(
  cameraId: string,
  input: CameraAssetHotspotInput,
  file?: File | null,
): Promise<CameraAssetHotspot> {
  const cid = String(cameraId || "").trim();
  if (!cid) throw new Error("Camera id is required.");

  const showType = input.show_type;
  let data: unknown;

  if (
    showType === "website" ||
    showType === "youtube" ||
    showType === "camera" ||
    showType === "text" ||
    showType === "api_url"
  ) {
    const body: CreateCameraHotspotJsonBody = {
      asset_name: String(input.asset_name ?? "").trim(),
      show_type: showType,
      yaw_deg: Number(input.yaw_deg),
      pitch_deg: Number(input.pitch_deg),
      pin_direction_deg: Number(input.pin_direction_deg) || 0,
      content_url: String(input.content_url ?? "").trim(),
    };
    data = await apiFetch(hotspotsPath(cid), { method: "POST", jsonBody: body });
  } else {
    if (!file) throw new Error("File is required for image or PDF hotspots.");
    const form = new FormData();
    form.append("file", file);
    form.append(
      "metadata",
      JSON.stringify({
        asset_name: String(input.asset_name ?? "").trim(),
        show_type: showType,
        yaw_deg: Number(input.yaw_deg),
        pitch_deg: Number(input.pitch_deg),
        pin_direction_deg: Number(input.pin_direction_deg) || 0,
      } satisfies CameraHotspotMetadataBody),
    );
    data = await apiFetch(hotspotsPath(cid), { method: "POST", body: form });
  }

  const mapped = mapCameraHotspotFromApi(unwrapHotspotEntity(data));
  if (!mapped) {
    throw new Error("Hotspot created but response was invalid.");
  }
  return mapped;
}

/** PUT /api/cameras/:camera_id/hotspots/:hotspot_id */
export async function updateCameraHotspot(
  cameraId: string,
  hotspotId: string,
  input: Partial<CameraAssetHotspotInput> & { content_url?: string },
  file?: File | null,
): Promise<CameraAssetHotspot> {
  const cid = String(cameraId || "").trim();
  const hid = String(hotspotId || "").trim();
  if (!cid || !hid) throw new Error("Camera id and hotspot id are required.");

  let data: unknown;

  if (file) {
    const form = new FormData();
    form.append("file", file);
    const metadata: Record<string, unknown> = {};
    if (input.asset_name != null) metadata.asset_name = String(input.asset_name).trim();
    if (input.show_type === "image" || input.show_type === "pdf") {
      metadata.show_type = input.show_type;
    }
    if (Number.isFinite(Number(input.yaw_deg))) metadata.yaw_deg = Number(input.yaw_deg);
    if (Number.isFinite(Number(input.pitch_deg))) metadata.pitch_deg = Number(input.pitch_deg);
    if (input.pin_direction_deg != null) {
      metadata.pin_direction_deg = Number(input.pin_direction_deg) || 0;
    }
    if (Object.keys(metadata).length > 0) {
      form.append("metadata", JSON.stringify(metadata));
    }
    data = await apiFetch(hotspotsPath(cid, hid), { method: "PUT", body: form });
  } else {
    const body: Record<string, unknown> = {};
    if (input.asset_name != null) body.asset_name = String(input.asset_name).trim();
    if (Number.isFinite(Number(input.yaw_deg))) body.yaw_deg = Number(input.yaw_deg);
    if (Number.isFinite(Number(input.pitch_deg))) body.pitch_deg = Number(input.pitch_deg);
    if (input.pin_direction_deg != null) {
      body.pin_direction_deg = Number(input.pin_direction_deg) || 0;
    }
    if (input.content_url != null) body.content_url = String(input.content_url).trim();
    const st = input.show_type;
    if (
      st === "website" ||
      st === "youtube" ||
      st === "camera" ||
      st === "text" ||
      st === "api_url" ||
      st === "image" ||
      st === "pdf"
    ) {
      body.show_type = st;
    }
    data = await apiFetch(hotspotsPath(cid, hid), { method: "PUT", jsonBody: body });
  }

  const mapped = mapCameraHotspotFromApi(unwrapHotspotEntity(data));
  if (!mapped) {
    throw new Error("Hotspot updated but response was invalid.");
  }
  return mapped;
}

/** DELETE /api/cameras/:camera_id/hotspots/:hotspot_id */
export async function deleteCameraHotspotApi(
  cameraId: string,
  hotspotId: string,
): Promise<void> {
  await apiFetch(hotspotsPath(cameraId, hotspotId), { method: "DELETE" });
}
